// Boiler plate for allowing us to construct a generic type
export type Constructor<T> = {
  new (...args: any[]): T;
};
// eslint-disable-next-line @typescript-eslint/ban-types
export type Abstract<T> = Function & { prototype: T };
export type Class<T> = Abstract<T> | Constructor<T>;

export type Mutable<T> = {
  -readonly [K in keyof T]: T[K];
};
export type Immutable<T> = {
  +readonly [K in keyof T]: T[K];
};

// Needed if you want to "extend" multiple classes
export function applyMixins(derivedCtor: any, baseCtors: any[]) {
  baseCtors.forEach((baseCtor) => {
    Object.getOwnPropertyNames(baseCtor.prototype).forEach((name) => {
      if (name !== "constructor") {
        derivedCtor.prototype[name] = baseCtor.prototype[name];
      }
    });
  });
}

import { List } from "immutable";

export enum types {
  "string",
  "number",
  "bigint",
  "boolean",
  "symbol",
  "undefined",
  "object",
  "function",
  "null",
  "date",
}

export interface SerializableConstructor<T extends SerializableClass> {
  /* Constructor signature. Any class that implements this interface must have a constructor (new) 
    that takes no arguments and returns an instance of type T. 
    This ensures that classes implementing SerializableConstructor 
    can be instantiated with a new keyword without any arguments. */
  new (): T;
  serializableFields: Map<string, types[]>;
}

export class SerializableClass {
  static serializableFields: Map<string, types[]> = new Map();

  // Can be used to initialize fields that aren't serialized
  protected initialize() {
    return;
  }

  constructor(...args: any[]) {}

  public static fromJSON<T extends SerializableClass>(
    this: SerializableConstructor<T>,
    json: any,
  ): T {
    const blueprint = new this();
    const constructorName = blueprint.constructor.name;
    let serializableFields = this.serializableFields;
    if (serializableFields.size === 0) {
      // If serializableFields is empty, assume all fields are serializable
      serializableFields = new Map<string, types[]>();
      for (const [key, value] of Object.entries(blueprint)) {
        serializableFields.set(key, [types[typeof value]]);
      }
    }
    const newObject = objectFromJSON(
      blueprint,
      json,
      constructorName,
      serializableFields,
    );
    newObject.initialize();
    return newObject;
  }
}

// Function takes a json object and converts it to some blueprint type
const objectFromJSON = <T extends SerializableClass>(
  blueprint: T,
  json: any,
  constructorName: string,
  serializableFields: Map<string, types[]>,
): T => {
  let property: keyof typeof blueprint;
  try {
    for (property in blueprint) {
      const propertyName = property.toString();
      const possibleTypes = serializableFields.get(property);
      const blueprintVal = blueprint[property];
      let jsonVal = json[property];

      // Skip any fields that aren't serializable
      if (typeof possibleTypes === "undefined") {
        continue;
      }

      const jsonType = jsonVal === null ? types.null : types[typeof jsonVal];

      if (!possibleTypes.includes(jsonType)) {
        throw new Error(
          "Following property for " +
            constructorName +
            " is incorrect type: " +
            propertyName,
        );
      }

      // If property is supposed to be an object, we need to do some more conversion
      if (typeof blueprintVal === "object" && blueprintVal !== null) {
        if (Array.isArray(blueprintVal)) {
          // Handle arrays
          // Arrays must contain values that are all of the same type
          jsonVal = arrayFromJSON(
            blueprintVal,
            json[property],
            propertyName,
            constructorName,
          );
        } else if (blueprintVal instanceof List) {
          const blueprintProperty = blueprintVal as any as List<any>;
          jsonVal = List(
            arrayFromJSON(
              blueprintProperty.toArray(),
              json[property],
              propertyName,
              constructorName,
            ),
          );
        } else if (blueprintVal instanceof SerializableClass) {
          // Convert json into instance of an object
          jsonVal = (
            blueprintVal.constructor as typeof SerializableClass
          ).fromJSON(jsonVal);
        } else if (blueprintVal instanceof Date) {
          const date = new Date();
          date.setTime(Date.parse(json[property]));
          jsonVal = date;
        } else {
          // Custom objects that aren't dates or arrays need fromJSON to be converted properly
          throw new Error(
            "Following property for " +
              constructorName +
              " is an object, but unable to convert object: " +
              propertyName +
              ". \n Check that the blueprint value is an array or implements fromJSON",
          );
        }
      }
      // Update blueprint values as we go
      blueprint[property] = jsonVal;
    }
  } catch (error: any) {
    throw new Error("Invalid JSON for " + constructorName + ": \n" + error);
  }
  return blueprint;
};

// Function takes a json array and converts it to some blueprint type
// Arrays could be made up of other arrays, and separate function allows
// us to call this recursively if necessary
const arrayFromJSON = (
  blueprint: Array<any>,
  jsonVals: Array<any>,
  propertyName: string,
  constructorName: string,
): Array<any> => {
  if (!Array.isArray(jsonVals)) {
    throw new Error(
      "Following property for " +
        constructorName +
        " should be an array but is not: " +
        propertyName,
    );
  }
  // If value is an array, make sure all values have same type
  // and that type matches what is in the blueprint
  const firstVal = blueprint[0];
  let returnVals = jsonVals;
  for (const val of jsonVals) {
    if (typeof firstVal !== typeof val) {
      throw new Error(
        "Following array property for " +
          constructorName +
          " has value that does not match type of blueprint: " +
          propertyName,
      );
    }
  }

  if (typeof firstVal === "object") {
    // If values are an object, convert each value
    if (Array.isArray(firstVal)) {
      returnVals = jsonVals.map((el) =>
        arrayFromJSON(firstVal, el, propertyName, constructorName),
      );
    } else if (firstVal instanceof SerializableClass) {
      // Call fromJSON on each element of the array
      returnVals = jsonVals.map((jsonVal) =>
        (firstVal.constructor as typeof SerializableClass).fromJSON(jsonVal),
      );
    } else if (firstVal instanceof Date) {
      returnVals = jsonVals.map((jsonVal) => {
        const date = new Date();
        date.setTime(Date.parse(jsonVal));
        return date;
      });
    } else {
      throw new Error(
        "Following array property for " +
          constructorName +
          " has objects, but unable to convert objects: " +
          propertyName +
          ". \n Check that the blueprint value is serializable",
      );
    }
  }
  return returnVals;
};

export class ImmutableClass extends SerializableClass {
  public static createInstance<T extends ImmutableClass>(
    this: Class<T>,
    data: Partial<T>,
  ): Immutable<T> {
    const instance = new (this as any)() as T;
    for (const [key, value] of Object.entries(data)) {
      if (typeof value !== "undefined") {
        (instance[key as keyof T] as any) = value;
      }
    }
    return instance;
  }

  public update<K extends keyof this>(key: K, value: this[K]): Immutable<this> {
    const copy = new (this.constructor as any)() as typeof this;
    for (const [key, value] of Object.entries(this)) {
      if (typeof value !== "undefined") {
        (copy[key as keyof this] as any) = value;
      }
    }
    copy[key] = value;
    return copy;
  }
}
